// Grids & Gantts — the ZQL grammar the query box walks. Pure, so node can
// test it (../../test/run-structure-engine-tests.mjs). It mirrors
// modules/database/zql/src/parser.js; see ./ZqlBox.jsx.

export const KW = new Set(['and', 'or', 'not', 'in', 'is', 'was', 'changed', 'empty', 'null', 'order', 'by', 'asc', 'desc']);
export const lower = (s) => String(s || '').toLowerCase();

// ---- tokens --------------------------------------------------------------------
export function tokenize(text) {
  const out = []; const n = text.length; let i = 0;
  while (i < n) {
    const ch = text[i];
    if (/\s/.test(ch)) { let j = i; while (j < n && /\s/.test(text[j])) j += 1; out.push({ type: 'ws', value: text.slice(i, j), start: i, end: j }); i = j; continue; }
    if (ch === '"' || ch === "'") {
      let j = i + 1; while (j < n && text[j] !== ch) { if (text[j] === '\\') j += 1; j += 1; }
      const closed = j < n; out.push({ type: 'string', value: text.slice(i + 1, closed ? j : n), raw: text.slice(i, closed ? j + 1 : n), start: i, end: closed ? j + 1 : n, unterminated: !closed }); i = closed ? j + 1 : n; continue;
    }
    if (ch === '(') { out.push({ type: 'lparen', value: '(', start: i, end: i + 1 }); i += 1; continue; }
    if (ch === ')') { out.push({ type: 'rparen', value: ')', start: i, end: i + 1 }); i += 1; continue; }
    if (ch === ',') { out.push({ type: 'comma', value: ',', start: i, end: i + 1 }); i += 1; continue; }
    const two = text.slice(i, i + 2);
    if (['!=', '<=', '>=', '!~'].includes(two)) { out.push({ type: 'op', value: two, start: i, end: i + 2 }); i += 2; continue; }
    if ('=<>~'.includes(ch)) { out.push({ type: 'op', value: ch, start: i, end: i + 1 }); i += 1; continue; }
    if (/[0-9]/.test(ch) && !/[A-Za-z_]/.test(text[i + 1] || '') && !/^[0-9]+-[0-9]/.test(text.slice(i))) { let j = i; while (j < n && /[0-9.]/.test(text[j])) j += 1; if (!/[A-Za-z_\-]/.test(text[j] || '')) { out.push({ type: 'number', value: text.slice(i, j), start: i, end: j }); i = j; continue; } }
    if (/[A-Za-z0-9_\-.\[\]]/.test(ch)) {
      let j = i; while (j < n && /[A-Za-z0-9_\-.\[\]]/.test(text[j])) j += 1;
      const v = text.slice(i, j);
      if (text[j] === '(' && !KW.has(lower(v))) {
        let k = j + 1; let q = null;
        while (k < n && (q || text[k] !== ')')) { if (q) { if (text[k] === '\\') k += 1; else if (text[k] === q) q = null; } else if (text[k] === '"' || text[k] === "'") q = text[k]; k += 1; }
        const closed = k < n;
        out.push({ type: 'func', value: text.slice(i, closed ? k + 1 : n), name: v, start: i, end: closed ? k + 1 : n, unterminated: !closed }); i = closed ? k + 1 : n; continue;
      }
      out.push({ type: KW.has(lower(v)) ? 'kw' : 'ident', value: v, start: i, end: j }); i = j; continue;
    }
    out.push({ type: 'bad', value: ch, start: i, end: i + 1 }); i += 1;
  }
  return out;
}

// ---- the grammar walk: where is the caret, what is wrong ------------------------
// Walks the non-space tokens up to `upto` (a character index, or the end)
// and answers { state, field, op, depth, listDepth, problems }. `state` is
// what the grammar expects NEXT: field · op · value · listValue · listSep ·
// join · hist · histValue · by · orderField · orderDir.
//
// HISTORY (WAS / CHANGED) is the engine's as well (parser.js parseHistPreds):
// `status WAS [NOT] [IN] value|(list)` and `status CHANGED`, each followed by
// any of FROM / TO / AFTER / BEFORE / ON / DURING / BY and a value (FROM / TO
// / DURING may take a bracketed list). Those words are only special in that
// place, so they are NOT keywords — `labels = after` stays a value.
// `filter = X` names a SAVED FILTER — the engine expands it before it
// compiles (zql/src/filter-clauses.js), so it is in no field registry, but it
// is a field to the person typing.
export const FILTER_FIELD = { name: 'filter', operators: ['=', '!=', 'in', 'not in'] };
export const HIST_PREDS = ['from', 'to', 'after', 'before', 'on', 'during', 'by'];
export function walk(tokens, registry, upto = Infinity) {
  let state = 'field'; let field = null; let op = null; let depth = 0; let inList = false; let lastNonWs = null;
  let hist = false; let pred = null;            // inside a WAS / CHANGED clause · the predicate whose value is owed
  const afterValue = () => (hist ? 'hist' : 'join');
  const problems = [];
  const fieldDef = (name) => registry.get(lower(name)) || registry.get(lower(name).replace(/\[.*\]$/, '')) || (lower(name) === FILTER_FIELD.name ? FILTER_FIELD : null);
  const push = (tok, message, fix) => problems.push({ pos: tok ? tok.start : 0, len: tok ? Math.max(1, tok.end - tok.start) : 1, message, fix });
  for (const t of tokens) {
    if (t.start >= upto) break;
    if (t.type === 'ws') continue;
    if (t.type === 'bad') { push(t, `"${t.value}" cannot be used here`, { label: `Remove "${t.value}"`, apply: (s) => s.slice(0, t.start) + s.slice(t.end) }); continue; }
    if (t.type === 'string' && t.unterminated) { push(t, 'This quote is never closed', { label: 'Close the quote', apply: (s) => s + t.raw[0] }); }
    if (t.type === 'func' && t.unterminated) { push(t, `${t.name}( is never closed`, { label: 'Close the bracket', apply: (s) => s.replace(/\s+$/, '') + ')' }); }
    const v = lower(t.value);
    switch (state) {
      case 'field':
        if (t.type === 'kw' && v === 'not') break;
        if (t.type === 'lparen') { depth += 1; break; }
        if (t.type === 'ident' || t.type === 'string') {
          field = t.value; op = null; hist = false; pred = null;
          if (registry.size && !fieldDef(t.value)) {
            const near = nearest(t.value, [...registry.values()].map((d) => d.name));
            push(t, `"${t.value}" is not a field on this site${near ? ` — did you mean ${near}?` : ''}`, near ? { label: `Use ${near}`, apply: (s) => s.slice(0, t.start) + near + s.slice(t.end) } : null);
          }
          state = 'op'; break;
        }
        if (t.type === 'kw' && (v === 'order')) { state = 'by'; break; }
        push(t, `Expected a field here, not "${t.value}"`); state = 'op'; break;
      case 'op':
        if (t.type === 'op') { op = t.value; state = 'value'; break; }
        if (t.type === 'kw' && v === 'not') { op = 'not'; break; }                 // not in / not ~ …
        if (t.type === 'kw' && v === 'in') { op = op === 'not' ? 'not in' : 'in'; state = 'value'; inList = false; break; }
        if (t.type === 'kw' && v === 'is') { op = 'is'; state = 'value'; break; }
        if (t.type === 'kw' && v === 'was') { op = 'was'; hist = true; state = 'value'; break; }
        if (t.type === 'kw' && v === 'changed') { op = 'changed'; hist = true; state = 'hist'; break; }
        if (t.type === 'op' || t.type === 'kw') { push(t, `"${t.value}" is not an operator`); break; }
        push(t, `Expected an operator after "${field}" — = != in is ~ < >`, null); state = 'value'; break;
      case 'value':
        if (t.type === 'func' && lower(t.name || '') === 'field' && /^(in|not in|~|!~)$/.test(lower(op || ''))) push(t, 'field() works only after = != < <= > >=');
        if (op && op.startsWith('was') && t.type === 'kw' && v === 'not' && op === 'was') { op = 'was not'; break; }
        if (op && op.startsWith('was') && t.type === 'kw' && v === 'in' && !/in$/.test(op)) { op = `${op} in`; break; }
        if (op === 'is') { if (t.type === 'kw' && v === 'not') break; if (t.type === 'kw' && (v === 'empty' || v === 'null')) { state = 'join'; break; } push(t, 'After IS only EMPTY or NULL may follow', { label: 'Use IS EMPTY', apply: (s) => s.slice(0, t.start) + 'EMPTY' + s.slice(t.end) }); state = 'join'; break; }
        if (t.type === 'lparen') { inList = true; state = 'listValue'; break; }
        if (t.type === 'ident' || t.type === 'string' || t.type === 'number' || t.type === 'func' || (t.type === 'kw' && (v === 'empty' || v === 'null'))) {
          if (/^(in|not in|was in|was not in)$/.test(op || '')) push(t, `${op.toUpperCase()} needs a bracketed list: ${field} ${op} (${t.value})`, { label: 'Add the brackets', apply: (s) => s.slice(0, t.start) + '(' + s.slice(t.start, t.end) + ')' + s.slice(t.end) });
          state = afterValue(); break;
        }
        push(t, `Expected a value after "${field} ${op}"`); state = afterValue(); break;
      case 'listValue':
        if (t.type === 'ident' || t.type === 'string' || t.type === 'number' || t.type === 'func' || t.type === 'kw') { state = 'listSep'; break; }
        if (t.type === 'rparen') { push(t, 'The list is empty'); inList = false; state = afterValue(); break; }
        push(t, `Expected a value in the list, not "${t.value}"`); state = 'listSep'; break;
      case 'listSep':
        if (t.type === 'comma') { state = 'listValue'; break; }
        if (t.type === 'rparen') { inList = false; state = afterValue(); break; }
        if (t.type === 'ident' || t.type === 'string' || t.type === 'number') { push(t, 'Values in a list are separated by commas', { label: 'Add the comma', apply: (s) => s.slice(0, lastNonWs.end) + ',' + s.slice(lastNonWs.end) }); break; }
        push(t, `Expected , or ) in the list, not "${t.value}"`); break;
      case 'hist':
        if ((t.type === 'ident' || t.type === 'kw') && HIST_PREDS.includes(v)) { pred = v; state = 'histValue'; break; }
        state = 'join';
        // falls through — anything else ends the history clause like any value
      case 'join':
        if (t.type === 'kw' && (v === 'and' || v === 'or')) { state = 'field'; break; }
        if (t.type === 'rparen') { depth -= 1; if (depth < 0) { depth = 0; push(t, 'This bracket closes nothing', { label: 'Remove it', apply: (s) => s.slice(0, t.start) + s.slice(t.end) }); } break; }
        if (t.type === 'kw' && v === 'order') { state = 'by'; break; }
        if (t.type === 'ident' || t.type === 'string') { push(t, `Join clauses with AND or OR before "${t.value}"`, { label: 'Insert AND', apply: (s) => s.slice(0, t.start) + 'AND ' + s.slice(t.start) }); field = t.value; state = 'op'; break; }
        push(t, `Expected AND, OR or ORDER BY, not "${t.value}"`); break;
      case 'histValue':
        if (t.type === 'lparen') { inList = true; state = 'listValue'; break; }
        if (t.type === 'ident' || t.type === 'string' || t.type === 'number' || t.type === 'func') { pred = null; state = 'hist'; break; }
        push(t, `${String(pred || '').toUpperCase()} needs a value, not "${t.value}"`); state = 'hist'; break;
      case 'by':
        if (t.type === 'kw' && v === 'by') { state = 'orderField'; break; }
        push(t, 'ORDER must be followed by BY', { label: 'Insert BY', apply: (s) => s.slice(0, t.start) + 'BY ' + s.slice(t.start) }); state = 'orderField'; break;
      case 'orderField':
        if (t.type === 'ident' || t.type === 'string') {
          if (registry.size && !fieldDef(t.value)) {
            const near = nearest(t.value, [...registry.values()].map((d) => d.name));
            push(t, `"${t.value}" is not a field on this site${near ? ` — did you mean ${near}?` : ''}`, near ? { label: `Use ${near}`, apply: (s) => s.slice(0, t.start) + near + s.slice(t.end) } : null);
          }
          state = 'orderDir'; break;
        }
        push(t, 'ORDER BY needs a field'); break;
      case 'orderDir':
        if (t.type === 'kw' && (v === 'asc' || v === 'desc')) { state = 'orderNext'; break; }
        if (t.type === 'comma') { state = 'orderField'; break; }
        push(t, `Expected ASC, DESC or a comma, not "${t.value}"`); break;
      case 'orderNext':
        if (t.type === 'comma') { state = 'orderField'; break; }
        push(t, `Nothing may follow ${lastNonWs?.value || 'the sort'}`); break;
      default: break;
    }
    lastNonWs = t;
  }
  return { state, field, op, depth, inList, problems, last: lastNonWs, hist, pred };
}

// Problems only the END of the text shows (the walk above stops at tokens).
export function endProblems(text, w) {
  const out = [];
  const trimmed = text.replace(/\s+$/, '');
  if (!trimmed) return out;
  if (w.depth > 0) out.push({ pos: trimmed.length, len: 1, message: `${w.depth} bracket${w.depth === 1 ? ' is' : 's are'} not closed`, fix: { label: 'Close the bracket' + (w.depth === 1 ? '' : 's'), apply: (s) => s.replace(/\s+$/, '') + ')'.repeat(w.depth) } });
  // An open list is said ONCE — the state below says it with the better fix.
  if (w.inList && w.state !== 'listValue' && w.state !== 'listSep') out.push({ pos: trimmed.length, len: 1, message: 'The list is not closed', fix: { label: 'Close the list', apply: (s) => s.replace(/\s+$/, '') + ')' } });
  if (w.state === 'field' && w.last) out.push({ pos: w.last.start, len: w.last.end - w.last.start, message: `"${w.last.value}" needs a clause after it`, fix: { label: `Remove "${w.last.value}"`, apply: (s) => s.slice(0, w.last.start).replace(/\s+$/, '') } });
  if (w.state === 'op') out.push({ pos: trimmed.length, len: 1, message: `"${w.field}" needs an operator and a value`, fix: null });
  if (w.state === 'value') out.push({ pos: trimmed.length, len: 1, message: `"${w.field} ${w.op}" needs a value`, fix: null });
  if (w.state === 'listValue' || w.state === 'listSep') out.push({ pos: trimmed.length, len: 1, message: 'The list is not closed', fix: { label: 'Close the list', apply: (s) => s.replace(/[\s,]+$/, '') + ')' } });
  if (w.state === 'histValue') out.push({ pos: trimmed.length, len: 1, message: `${String(w.pred || '').toUpperCase()} needs a value`, fix: null });
  if (w.state === 'by') out.push({ pos: trimmed.length, len: 1, message: 'ORDER must be followed by BY', fix: { label: 'Add BY', apply: (s) => s.replace(/\s+$/, '') + ' BY ' } });
  if (w.state === 'orderField') out.push({ pos: trimmed.length, len: 1, message: 'ORDER BY needs a field', fix: null });
  return out;
}

// The closest known name (a prefix, or within a few edits), or null.
export function nearest(word, names) {
  const w = lower(word); if (!w) return null;
  const pre = names.filter((n) => lower(n).startsWith(w) || w.startsWith(lower(n)));
  if (pre.length) return pre.sort((a, b) => a.length - b.length)[0];
  let best = null; let bestD = Infinity;
  for (const n of names) { const d = lev(w, lower(n)); if (d < bestD) { bestD = d; best = n; } }
  return bestD <= Math.max(2, Math.floor(w.length / 3)) ? best : null;
}
function lev(a, b) {
  if (a === b) return 0; if (!a.length) return b.length; if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) { const cur = [i]; for (let j = 1; j <= b.length; j += 1) cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)); prev = cur; }
  return prev[b.length];
}
export const quoteIfNeeded = (v) => (/^[A-Za-z0-9_\-.]+$/.test(v) ? v : `"${String(v).replace(/"/g, '\\"')}"`);

// Values ranked against the typed text, on the value OR its description
// (a project's name, a person's display name); starts-with first.
export function rankValues(list, p) {
  if (!p) return list;
  const hay = (x) => `${lower(x.v)} ${lower(x.d)}`;
  const starts = (x) => (lower(x.v).startsWith(p) || lower(x.d).startsWith(p) ? 0 : 1);
  return list.filter((x) => hay(x).includes(p)).sort((a, b) => starts(a) - starts(b) || String(a.v).localeCompare(String(b.v)));
}
